import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../data-access/permission.service';
import { PermissionCode } from '../data-access/permission.types';
import { environment } from '../../../environments/environment';
import { ApiSessionService } from '../data-access/api/api-session.service';
import { ApiAuthService } from '../data-access/api/api-auth.service';
import { catchError, map } from 'rxjs/operators';

/**
 * Protects routes that require a specific permission code.
 * Add `data: { requiredRight: 'PERMISSION_CODE' }` to the route definition.
 * Redirects to /forbidden (AN-UX-83) when the active role lacks the required
 * right, so the user sees an explicit "Accès refusé" state instead of being
 * bounced silently to /dashboard.
 */
export const roleGuard: CanActivateFn = (route) => {
  const permissions = inject(PermissionService);
  const router      = inject(Router);
  const required    = route.data['requiredRight'] as PermissionCode | undefined;

  if (environment.dataSource === 'api') {
    const session = inject(ApiSessionService);
    const auth = inject(ApiAuthService);
    if (!auth.accessToken()) return router.createUrlTree(['/login']);
    if (!session.me()) {
      return session.ensureProfileLoaded().pipe(
        map(() => !required || permissions.hasRight(required) ? true : router.createUrlTree(['/forbidden'])),
        catchError(() => [router.createUrlTree(['/login'])]),
      );
    }
  }

  if (!required || permissions.hasRight(required)) return true;

  return router.createUrlTree(['/forbidden']);
};
